import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ChevronRight, Car, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeachCard from "@/components/BeachCard";
import BeachMap from "@/components/BeachMap";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getSeoArticleData } from "@/lib/api";
import { toast } from "sonner";

const BEACH_IMAGE = "/images/hero/beach-default.jpg";

const SeoArticlePage = () => {
  const { stateSlug, articleType, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getSeoArticleData(articleType, citySlug, stateSlug);
        setData(result);
      } catch (error) {
        console.error("Error fetching article data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [stateSlug, articleType, citySlug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: getArticleTitle(), text: getArticleDescription(), url });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const formatStateName = (slug) =>
    slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  const displayStateName = data?.state || formatStateName(stateSlug);
  const displayStateSlug = data?.state_slug || stateSlug;

  const getArticleTitle = () => {
    if (!data) return "";
    switch (articleType) {
      case "best-beach-access":
        return `Best Beach Access Points in ${data.city_name}, ${displayStateName}`;
      case "free-beach-access":
        return `Free Beach Access in ${data.city_name}, ${displayStateName}`;
      case "beach-access-with-parking":
        return `Beach Access with Parking in ${data.city_name}, ${displayStateName}`;
      default:
        return `Beach Access in ${data.city_name}, ${displayStateName}`;
    }
  };

  const getArticleDescription = () => {
    if (!data) return "";
    switch (articleType) {
      case "best-beach-access":
        return `${data.city_name}, ${displayStateName} has ${data.total} public beach access points. See our ranked guide to the best beaches with ratings, amenities like free parking and restrooms, interactive maps, and step-by-step directions. Updated ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.`;
      case "free-beach-access":
        return `Complete list of ${data.total} free public beach access points in ${data.city_name}, ${displayStateName}. No entry fees required. Find beaches with free parking, restroom facilities, outdoor showers, and wheelchair accessibility. Includes map and directions.`;
      case "beach-access-with-parking":
        return `Need beach parking in ${data.city_name}, ${displayStateName}? Browse ${data.total} public beach access points with dedicated parking lots and street parking. Includes free and paid options, restroom availability, and driving directions to each location.`;
      default:
        return `Guide to ${data.total} public beach access points in ${data.city_name}, ${displayStateName}. Amenities, maps, reviews, and directions.`;
    }
  };

  const getArticleIntro = () => {
    if (!data) return "";
    switch (articleType) {
      case "best-beach-access":
        return (
          <>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4">
              Looking for the best beach access in {data.city_name}, {displayStateName}? You've come to the right place!
              We've compiled a comprehensive guide to {data.total} public beach access points in and around {data.city_name},
              so you can find the perfect spot for your next beach day.
            </p>
            <p className="text-gray-600 leading-relaxed">
              From popular sandy shores to hidden gems, each beach access point includes information about
              parking, restrooms, showers, and accessibility features. Use our interactive map and detailed
              listings to discover your new favorite beach.
            </p>
          </>
        );
      case "free-beach-access":
        return (
          <>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4">
              Enjoying {displayStateName}'s beautiful beaches doesn't have to break the bank! {data.city_name} offers
              {data.total} public beach access points where you can enjoy sun, sand, and surf without expensive entry fees.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Many beach access points offer free parking or affordable rates. Explore our list to find
              the best free beach access in {data.city_name}.
            </p>
          </>
        );
      case "beach-access-with-parking":
        return (
          <>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4">
              One of the biggest challenges of visiting beaches is finding parking. We've made it easy by
              listing {data.total} beach access points in {data.city_name} that offer parking facilities.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you need free street parking, metered spots, or dedicated beach parking lots, our
              directory helps you plan ahead so you can spend less time circling for parking and more time
              enjoying the beach.
            </p>
          </>
        );
      default:
        return null;
    }
  };

  const getListingTitle = () => {
    switch (articleType) {
      case "best-beach-access":
        return `Top Beach Access Points in ${data?.city_name}`;
      case "free-beach-access":
        return `Free Beach Access in ${data?.city_name}`;
      case "beach-access-with-parking":
        return `Beaches with Parking in ${data?.city_name}`;
      default:
        return `Beach Access in ${data?.city_name}`;
    }
  };

  const getBreadcrumbLabel = () => {
    switch (articleType) {
      case "best-beach-access": return "Best Beach Access";
      case "free-beach-access": return "Free Beach Access";
      case "beach-access-with-parking": return "Beach Access with Parking";
      default: return "Beach Access";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50">
        <div className="bg-white border-b border-gray-200 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="h-6 w-64 bg-gray-200 animate-pulse rounded mb-4" />
            <div className="h-10 w-96 bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="h-[400px] bg-gray-200 animate-pulse rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏖️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <Link to="/search">
            <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]">Explore Beaches</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="seo-article-page">
      <SeoHead
        title={getArticleTitle()}
        description={getArticleDescription()}
        path={`/${displayStateSlug}/${articleType}/${citySlug}`}
        type="article"
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <nav className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 overflow-x-auto">
            <Link to="/" className="hover:text-[#0EA5E9] whitespace-nowrap">Home</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <Link to={`/state/${displayStateSlug}`} className="hover:text-[#0EA5E9] whitespace-nowrap">{displayStateName}</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <Link to={`/state/${displayStateSlug}/${citySlug}`} className="hover:text-[#0EA5E9] whitespace-nowrap">{data.city_name}</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="text-gray-800 font-medium whitespace-nowrap truncate max-w-[150px] sm:max-w-none">
              {getBreadcrumbLabel()}
            </span>
          </nav>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-800 font-['Outfit'] tracking-tight">
                {getArticleTitle()}
              </h1>
              <p className="text-gray-500 text-sm sm:text-base mt-1">
                {data.total} beach access points &bull; Updated {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" onClick={handleShare} className="border-2 border-gray-200 hover:bg-gray-50 text-sm h-9 sm:h-10" size="sm">
                <Share2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Link to={`/state/${displayStateSlug}/${citySlug}`}>
                <Button className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold text-sm h-9 sm:h-10" size="sm">
                  <MapPin className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">All {data.city_name} Beaches</span>
                  <span className="sm:hidden">View All</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="h-2 bg-[#0EA5E9]" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid lg:grid-cols-5 gap-6 sm:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-3 space-y-6">
            {/* Hero Image */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
              <img src={BEACH_IMAGE} alt={`Beach in ${data.city_name}, ${displayStateName}`} className="w-full h-[200px] sm:h-[300px] lg:h-[350px] object-cover" />
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                <span className="bg-[#10B981] text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm shadow-lg">
                  {data.total} Beaches Listed
                </span>
              </div>
              {articleType === "beach-access-with-parking" && (
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                  <span className="bg-blue-500 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm shadow-lg flex items-center gap-1">
                    <Car className="w-4 h-4" /> Parking Available
                  </span>
                </div>
              )}
            </div>

            {/* Article Intro */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Overview</h2>
              {getArticleIntro()}
            </div>

            {/* Beach Listings */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                {getListingTitle()}
              </h2>
              <div className="grid gap-3 sm:gap-4">
                {data.beaches.map((beach, index) => (
                  <div key={beach.id} className="relative">
                    {articleType === "best-beach-access" && index < 3 && (
                      <div className="absolute -top-2 -left-2 w-8 h-8 bg-[#F97316] text-white rounded-full flex items-center justify-center font-bold text-sm z-10 shadow-md">
                        #{index + 1}
                      </div>
                    )}
                    <BeachCard beach={beach} />
                  </div>
                ))}
              </div>
            </div>

            {/* Tips Section */}
            <div className="bg-[#FEF3C7] rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                Tips for Visiting {data.city_name} Beaches
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-xl">☀️</span>
                  <span>Arrive early on weekends to secure parking, especially during peak season.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xl">🧴</span>
                  <span>Don't forget sunscreen! The sun is strong, even on cloudy days.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xl">🌊</span>
                  <span>Check local beach conditions and any flags or warnings before entering the water.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xl">🚗</span>
                  <span>Look for free street parking or consider arriving by bike to avoid parking fees.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Map */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Location Map</h2>
              <div className="h-[250px] sm:h-[300px] rounded-xl overflow-hidden">
                <BeachMap beaches={data.beaches} />
              </div>
            </div>

            {/* Related Articles */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                More in {data.city_name}
              </h2>
              <div className="space-y-3">
                {articleType !== "best-beach-access" && (
                  <Link to={`/${displayStateSlug}/best-beach-access/${citySlug}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-[#DBEAFE] transition-colors group">
                    <span className="font-medium text-gray-800 group-hover:text-[#0EA5E9]">Best Beach Access</span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#0EA5E9]" />
                  </Link>
                )}
                {articleType !== "free-beach-access" && (
                  <Link to={`/${displayStateSlug}/free-beach-access/${citySlug}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-[#DBEAFE] transition-colors group">
                    <span className="font-medium text-gray-800 group-hover:text-[#0EA5E9]">Free Beach Access</span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#0EA5E9]" />
                  </Link>
                )}
                {articleType !== "beach-access-with-parking" && (
                  <Link to={`/${displayStateSlug}/beach-access-with-parking/${citySlug}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-[#DBEAFE] transition-colors group">
                    <span className="font-medium text-gray-800 group-hover:text-[#0EA5E9]">Beach Access with Parking</span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#0EA5E9]" />
                  </Link>
                )}
                <Link to={`/state/${displayStateSlug}/${citySlug}`} className="flex items-center justify-between p-3 bg-[#DBEAFE] rounded-xl hover:bg-[#BFDBFE] transition-colors group">
                  <span className="font-medium text-[#0EA5E9]">All {data.city_name} Beaches</span>
                  <ChevronRight className="w-5 h-5 text-[#0EA5E9]" />
                </Link>
              </div>
            </div>

            {/* Explore Other Cities */}
            {data.related_cities.length > 0 && (
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                  Other {displayStateName} Beaches
                </h2>
                <div className="space-y-2">
                  {data.related_cities.map((city) => (
                    <Link
                      key={city.slug}
                      to={`/${displayStateSlug}/${articleType}/${city.slug}`}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-[#DBEAFE] transition-colors group"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#0EA5E9]" />
                        <span className="font-medium text-gray-800 group-hover:text-[#0EA5E9]">{city.name}</span>
                      </div>
                      <span className="text-sm text-gray-500">{city.beach_count} beaches</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SeoArticlePage;
